import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, App as AntdApp, Form, Input, Modal, Typography } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { organizationsAccessDestroyMutation } from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserQueries } from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

const { Text } = Typography

type Props = {
  workspaceId: number
  open: boolean
  user: WorkspaceUser | null
  onClose: () => void
  onDeleted?: () => void
}

type FormValues = { confirmation: string }

export const UserDeleteModal = ({
  workspaceId,
  open,
  user,
  onClose,
  onDeleted
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm<FormValues>()
  const confirmation = Form.useWatch('confirmation', form) ?? ''

  const expected = user?.user.email ?? ''
  const matches =
    expected.length > 0 &&
    confirmation.trim().toLowerCase() === expected.toLowerCase()

  const { mutateAsync, isPending } = useMutation({
    ...organizationsAccessDestroyMutation(),
    onSuccess: async () => {
      message.success(t('users.modals.delete.success'))
      if (user) {
        await invalidateWorkspaceUserQueries(qc, {
          workspaceId,
          organizationAccessId: user.id,
          userId: user.user.id
        })
      }
      onClose()
      onDeleted?.()
    },
    onError: (e) => showApiErrors(e, form)
  })

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({ confirmation: '' })
  }, [open, form])

  const submit = async () => {
    if (!user || !matches) return
    await mutateAsync({ path: { id: user.id } })
  }

  const name = user
    ? composeDisplayName(
        user.user.first_name,
        user.user.last_name,
        user.user.email
      )
    : ''

  return (
    <Modal
      open={open}
      centered
      title={t('users.modals.delete.title')}
      okText={t('users.modals.delete.submit')}
      cancelText={t('users.modals.cancel')}
      okType="danger"
      okButtonProps={{ loading: isPending, disabled: !matches }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={() => form.submit()}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t('users.modals.delete.description', { name })}
      </p>
      <Alert
        type="error"
        showIcon
        title={t('users.modals.delete.warning')}
        style={{ marginBottom: 12 }}
      />
      <Form<FormValues> form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="confirmation"
          label={
            <span>
              {t('users.modals.delete.confirmLabel')}{' '}
              <Text code style={{ fontSize: 12 }}>
                {expected}
              </Text>
            </span>
          }
        >
          <Input autoComplete="off" placeholder={expected} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
